/**
 * The wuxia village's starter loop as data (docs/archive/CLIENT_V1_PLAN.md A3 S2; judgement, docs/research/STARTER_COMBAT.md
 * and WUXIA_WORLD.md §2): the body (`VILLAGE_PHYS`), the flow and feel (`STARTER`), the boars' AI tuning
 * (`STARTER_MOB`), the sample's world-boss scaling (`STARTER_APE`). The rules are in src/village/*.
 */

/** The heroine's body: walk, step, jump (apex height and time, Pittman GDC 2016), forgiveness, dash. */
export const VILLAGE_PHYS = {
  /** Walk speed (m/s). */
  speed: 4.2,
  /** Step-up and snap-down heights (m). */
  step: 0.42,
  snap: 0.35,
  bodyR: 0.38,
  bodyH: 1.75,
  /** Jump: apex height (m, full hold) and time to apex (s); fall gravity ×, cut on release, terminal fall. */
  jumpH: 2.4,
  jumpT: 0.36,
  fallK: 1.5,
  cut: 0.6,
  terminal: 22,
  /** Forgiveness (s). */
  coyote: 0.1,
  buffer: 0.1,
  /** Dash: length (s), speed (m/s), cooldown (s, from the dash's start), hitch at the start (s). */
  dashT: 0.18,
  dashV: 15,
  dashCd: 0.45,
  hitch: 0.04,
} as const;

/** The starter flow: spawn → boar quest at the yard → countdown → the ape in the west field → victory. */
export const STARTER = {
  /** The village's rule seed (one `mulberry32` stream for every roll). */
  seed: 20261009,
  spawn: [3, 24] as const,
  yard: [-14, -7] as const,
  /** The quest's monster (data/monsters.ts) and boar homes round the training yard. */
  monster: 'boar-starter',
  boars: [
    [-16, -5],
    [-12, -4.5],
    [-18.5, -8.5],
    [-10.5, -8.5],
    [-15, -10],
    [-6.5, -4],
  ] as const,
  /** The quest row (data/quests.ts `STARTER_QUEST`) holds the count; the boss lands here. */
  arena: [-24, 14] as const,
  /** Seconds of the intro before the quest opens; countdown to the boss after the banner (s). */
  intro: 2,
  bossBanner: 2.6,
  bossCountdown: 5,
  /** The death: seconds before the respawn at the spawn. */
  deathT: 3,
  hitStop: 0.07,
  hitStopCrit: 0.11,
  knock: 1.2,
  knockSpin: 2,
  /** Out of combat after this many seconds: faster regen. */
  calm: 5,
  hpCalmPerSec: 0.05,
  /** The soft aim: turn to the nearest foe within this many metres when casting. */
  aim: 6,
  /** Feet this high dodge a boar's gore (jumping over it). */
  jumpClear: 0.8,
  /** A boar's crit chance on the heroine. */
  mobCrit: 0.05,
  /** Moving while casting (share of walk speed). */
  castMove: 0.25,
} as const;

/** The boars' AI tuning (the stats are the `boar-starter` row). */
export const STARTER_MOB = {
  radius: 0.55,
  wander: 3,
  wanderSpeed: 1.1,
  returnSpeed: 4,
  stagger: 0.22,
  knockTime: 0.15,
  deathFade: 0.7,
  /** The charge: lunge length (m) and time (s), lane width (m). */
  chargeLen: 4.2,
  chargeTime: 0.32,
  chargeW: 1.3,
  /** The gore cone's half angle. */
  goreHalf: 0.95,
} as const;

/** The world boss in the village (`wb-ape`, one fresh player): HP, reach ×0.6 (an 11 m arena), enrage at 120 s. */
export const STARTER_APE = {
  hp: 1800,
  reachK: 0.6,
  arenaR: 11,
  enrageAt: 120,
  radius: 1.5,
  /** Keeps this far from the player while choosing (m). */
  keep: 2.6,
  /** A ring is jumped over when the feet are this high at the strike (the slam's answer: jump over or dash out). */
  jumpClear: 0.7,
  /** Seconds between a move's end and the next choice. */
  gap: 0.9,
  knockK: 0.5,
  scale: 1.0,
} as const;
